/**
 * Analytics i18n — FR default + AR-RTL. Registered once in the module's onRegister(). Keys are
 * namespaced under `analytics.*` and `nav.analytics.*`. Layout mirrors automatically under dir="rtl"
 * via CSS logical properties; numbers / currency / dates stay LTR with .nv-ltr-island.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.analytics": "Pilotage",
  "nav.analytics.tableau": "Tableau analytique",

  // page chrome
  "analytics.title": "Tableau analytique",
  "analytics.subtitle": "Indicateurs de pilotage du laboratoire — lecture seule, consultation auditée.",
  "analytics.readonly.note": "Vue lecture seule. Chaque consultation est journalisée (audit-on-read).",
  "analytics.action.refresh": "Actualiser",
  "analytics.updated": "Mis à jour {time}",

  // period bascule
  "analytics.period.label": "Période",
  "analytics.period.ytd": "Depuis le 1ᵉʳ janvier",
  "analytics.period.ytd.short": "YTD",
  "analytics.period.3mo": "3 mois",
  "analytics.period.12mo": "12 mois",

  // tone bands (rapport tone)
  "analytics.tone.good": "Conforme",
  "analytics.tone.watch": "À surveiller",
  "analytics.tone.alert": "Hors cible",

  // tiles section
  "analytics.tiles.title": "Indicateurs du jour",
  "analytics.tiles.live": "Aujourd'hui (temps réel)",
  "analytics.tiles.subtitle": "Agrégats du jour en cours, recalculés à la lecture.",

  // metrics
  "analytics.metric.tat_signoff": "Délai de rendu (validation)",
  "analytics.metric.tat_signoff.unit": "min",
  "analytics.metric.volume_demandes": "Volume — demandes",
  "analytics.metric.volume_resultats": "Volume — résultats",
  "analytics.metric.revenu_caisse": "Recette caisse",
  "analytics.metric.qc_rejet_taux": "Taux de rejet CQ",
  "analytics.metric.reactif_depense": "Consommation réactifs",
  "analytics.metric.count.suffix": "n = {n}",

  // NOVA-037 — per-discipline TAT view
  "analytics.discipline.title": "Délai de rendu par discipline",
  "analytics.discipline.subtitle": "Délai moyen de validation, ventilé par discipline (agrégat, sans ligne patient).",
  "analytics.discipline.col.code": "Discipline",
  "analytics.discipline.col.tat": "Délai moyen (min)",
  "analytics.discipline.col.count": "Validations",
  "analytics.discipline.col.tone": "État",
  "analytics.discipline.empty": "Aucun délai par discipline pour la période.",

  // NOVA-038 — export
  "analytics.export.label": "Exporter",
  "analytics.export.json": "JSON",
  "analytics.export.csv": "CSV",
  "analytics.export.xlsx": "XLSX",
  "analytics.export.aria": "Exporter le tableau au format {format}",
  "analytics.export.error": "Échec de l'export. Réessayez.",

  // trend chart
  "analytics.trend.title": "Tendance",
  "analytics.trend.pick": "Indicateur",
  "analytics.trend.empty.title": "Aucun historique",
  "analytics.trend.empty.desc":
    "Aucun instantané n'a encore été enregistré pour cette période. Les tendances apparaîtront après le premier instantané quotidien.",
  "analytics.trend.points": "{n} instantané(s)",

  // referral federation tile (cloud-only — hidden when absent)
  "analytics.referral.title": "Réseau de médecins référents",
  "analytics.referral.subtitle": "Fédéré depuis le module de référence (cloud).",
  "analytics.referral.volume": "Médecins actifs",
  "analytics.referral.retours": "Retours",
  "analytics.referral.col.medecin": "Médecin",
  "analytics.referral.col.retours": "Retours",
  "analytics.referral.empty": "Aucun médecin référent actif.",

  // 5-state copy
  "analytics.empty.title": "Aucun indicateur",
  "analytics.empty.desc":
    "Aucune donnée analytique pour ce laboratoire. Les indicateurs apparaîtront dès la première activité.",
  "analytics.offline.note": "Indicateurs affichés depuis le cache local.",

  // cloud-absent state (module not mounted on this install)
  "analytics.absent.title": "Indisponible sur cette installation",
  "analytics.absent.desc":
    "Le tableau analytique n'est pas activé sur ce poste. Ce module est fourni sur les installations connectées (cloud).",
};

const AR: Record<string, string> = {
  // nav
  "nav.section.analytics": "القيادة",
  "nav.analytics.tableau": "اللوحة التحليلية",

  // page chrome
  "analytics.title": "اللوحة التحليلية",
  "analytics.subtitle": "مؤشرات قيادة المخبر — للقراءة فقط، استشارة مدقّقة.",
  "analytics.readonly.note": "عرض للقراءة فقط. كل استشارة تُسجَّل (تدقيق عند القراءة).",
  "analytics.action.refresh": "تحديث",
  "analytics.updated": "آخر تحديث {time}",

  // period bascule
  "analytics.period.label": "الفترة",
  "analytics.period.ytd": "منذ فاتح يناير",
  "analytics.period.ytd.short": "YTD",
  "analytics.period.3mo": "3 أشهر",
  "analytics.period.12mo": "12 شهرا",

  // tone bands
  "analytics.tone.good": "مطابق",
  "analytics.tone.watch": "للمراقبة",
  "analytics.tone.alert": "خارج الهدف",

  // tiles section
  "analytics.tiles.title": "مؤشرات اليوم",
  "analytics.tiles.live": "اليوم (في الوقت الحقيقي)",
  "analytics.tiles.subtitle": "تجميعات اليوم الجاري، يُعاد حسابها عند القراءة.",

  // metrics
  "analytics.metric.tat_signoff": "زمن الإنجاز (المصادقة)",
  "analytics.metric.tat_signoff.unit": "دقيقة",
  "analytics.metric.volume_demandes": "الحجم — الطلبات",
  "analytics.metric.volume_resultats": "الحجم — النتائج",
  "analytics.metric.revenu_caisse": "مداخيل الصندوق",
  "analytics.metric.qc_rejet_taux": "نسبة رفض ضبط الجودة",
  "analytics.metric.reactif_depense": "استهلاك الكواشف",
  "analytics.metric.count.suffix": "ن = {n}",

  // NOVA-037 — per-discipline TAT view
  "analytics.discipline.title": "زمن الإنجاز حسب التخصص",
  "analytics.discipline.subtitle": "متوسط زمن المصادقة، موزّع حسب التخصص (تجميع، دون سطر مريض).",
  "analytics.discipline.col.code": "التخصص",
  "analytics.discipline.col.tat": "المتوسط (دقيقة)",
  "analytics.discipline.col.count": "المصادقات",
  "analytics.discipline.col.tone": "الحالة",
  "analytics.discipline.empty": "لا يوجد زمن إنجاز حسب التخصص لهذه الفترة.",

  // NOVA-038 — export
  "analytics.export.label": "تصدير",
  "analytics.export.json": "JSON",
  "analytics.export.csv": "CSV",
  "analytics.export.xlsx": "XLSX",
  "analytics.export.aria": "تصدير اللوحة بصيغة {format}",
  "analytics.export.error": "فشل التصدير. أعد المحاولة.",

  // trend chart
  "analytics.trend.title": "الاتجاه",
  "analytics.trend.pick": "المؤشر",
  "analytics.trend.empty.title": "لا يوجد سجل",
  "analytics.trend.empty.desc":
    "لم تُسجَّل بعد أي لقطة لهذه الفترة. ستظهر الاتجاهات بعد أول لقطة يومية.",
  "analytics.trend.points": "{n} لقطة",

  // referral federation tile
  "analytics.referral.title": "شبكة الأطباء المُحيلين",
  "analytics.referral.subtitle": "مُجمَّعة من وحدة الإحالة (السحابة).",
  "analytics.referral.volume": "أطباء نشطون",
  "analytics.referral.retours": "الإرجاعات",
  "analytics.referral.col.medecin": "الطبيب",
  "analytics.referral.col.retours": "الإرجاعات",
  "analytics.referral.empty": "لا يوجد طبيب مُحيل نشط.",

  // 5-state copy
  "analytics.empty.title": "لا توجد مؤشرات",
  "analytics.empty.desc": "لا توجد بيانات تحليلية لهذا المخبر. ستظهر المؤشرات مع أول نشاط.",
  "analytics.offline.note": "مؤشرات معروضة من الذاكرة المحلية.",

  // cloud-absent state
  "analytics.absent.title": "غير متاح على هذا التثبيت",
  "analytics.absent.desc":
    "اللوحة التحليلية غير مفعّلة على هذا الجهاز. تُوفَّر هذه الوحدة على التثبيتات المتصلة (السحابة).",
};

let registered = false;

/** Idempotent — registers the analytics namespace into both dictionaries exactly once. */
export function registerAnalyticsMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
